import { useEffect, useRef } from 'react';

import { useActivityCenterTracking } from '@xing-com/crate-activity-center-tracking';
import {
  useActivityCenterButtonRendered,
  useActivityCenterTotal,
  useIsActivityCenterVisible,
  useSetActivityCenterVisible,
} from '@xing-com/hub';
import { addParams } from '@xing-com/xing-tracking';

import * as Styled from './activity-center-login-button.styled';
import { SuperEllipseWithTopBadge } from './super-ellipse-with-top-badge';

type ActivityCenterButtonProps = {
  title: string;
  href?: string;
  loginLink?: string;
};

export const ActivityCenterLoginButton: React.FC<ActivityCenterButtonProps> = ({
  title,
  href,
  loginLink,
}) => {
  useActivityCenterButtonRendered();

  const total = useActivityCenterTotal();
  const setActivityCenterVisible = useSetActivityCenterVisible();
  const isActivityCenterVisible = useIsActivityCenterVisible();
  const { trackInteraction } = useActivityCenterTracking();

  const justClosedRef = useRef<boolean>(false);
  const trackingURL =
    loginLink &&
    addParams(loginLink, {
      nwt_nav: 'activitycenter',
      signup_channel: 'minireg_activitycenter',
    });
  const navigateTo = total <= 0 && trackingURL ? trackingURL : href;

  useEffect(() => {
    if (isActivityCenterVisible) return;

    justClosedRef.current = true;

    const timerId = setTimeout(() => {
      justClosedRef.current = false;
    }, 100);

    return () => {
      clearTimeout(timerId);
      justClosedRef.current = false;
    };
  }, [isActivityCenterVisible]);

  const onClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    if (total > 0) {
      event.preventDefault();

      if (!justClosedRef.current) {
        setActivityCenterVisible(!isActivityCenterVisible);
      }

      trackInteraction({
        event: 'clicked',
        element: 'activitycenter',
        elementDetail: 'activitycenter_open',
      });
    }
  };

  return (
    <Styled.Link
      id="activity-center-button"
      data-testid="frame-vnav-activity-center-login-button"
      to={navigateTo}
      aria-label={title}
      onClick={onClick}
    >
      <SuperEllipseWithTopBadge count={total} />
      {title}
    </Styled.Link>
  );
};
